import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Command } from 'cmdk';
import { Icon } from '@/shared/icons';
import { useCompanyDrawer } from '@/contexts/CompanyDrawerContext';
import { usePermissions } from '@/hooks/usePermissions';
import type { AccessRule } from '@/lib/route-access';
import type { IconName } from '@/shared/icons';

/**
 * Item de navegação do command palette
 */
interface CommandItem {
  id: string;
  label: string;
  /** Destino da navegação. Ausente quando o item executa uma AÇÃO (`action`). */
  href?: string;
  /**
   * Ação a executar no lugar de navegar. Existe porque nem todo destino do app é
   * uma rota: "Empresa" abre uma gaveta, e antes disso o item apontava para
   * `/settings/company` — endereço que nunca existiu — e caía no NotFound.
   */
  action?: 'open-company-drawer';
  iconName: IconName;
  group: string;
  keywords?: string[];
}

/**
 * Items de navegação disponíveis
 */
const navigationItems: CommandItem[] = [
  // Principal
  { id: 'dashboard', label: 'Dashboard', href: '/', iconName: 'home', group: 'Navegação', keywords: ['inicio', 'home', 'painel'] },
  { id: 'atendimento', label: 'Atendimento', href: '/chat/management', iconName: 'message-square', group: 'Navegação', keywords: ['chat', 'conversa', 'suporte'] },
  { id: 'chat', label: 'Mensagens', href: '/chat', iconName: 'message-square', group: 'Navegação', keywords: ['mensagem', 'conversa'] },

  // Comercial
  { id: 'crm', label: 'CRM', href: '/crm', iconName: 'trending-up', group: 'Comercial', keywords: ['leads', 'funil', 'vendas', 'pipeline'] },
  { id: 'agenda', label: 'Agenda', href: '/calendar', iconName: 'calendar', group: 'Comercial', keywords: ['calendario', 'evento', 'reuniao', 'compromisso'] },
  { id: 'ranking', label: 'Ranking', href: '/crm/ranking', iconName: 'star', group: 'Comercial', keywords: ['ranking', 'pontos', 'podio', 'vendedores', 'gamificacao'] },

  // Produtividade
  { id: 'tickets', label: 'Tickets', href: '/helpdesk/tickets', iconName: 'headphones', group: 'Produtividade', keywords: ['ticket', 'chamado', 'suporte'] },
  { id: 'campanhas', label: 'Campanhas', href: '/prospecting/campaigns', iconName: 'target', group: 'Produtividade', keywords: ['campanha', 'prospeccao', 'envio'] },
  { id: 'templates', label: 'Templates', href: '/prospecting/templates', iconName: 'target', group: 'Produtividade', keywords: ['template', 'modelo', 'mensagem'] },

  // Automação
  { id: 'workflows', label: 'Workflows', href: '/automation/workflows', iconName: 'zap', group: 'Automação', keywords: ['automacao', 'fluxo', 'bot'] },
  { id: 'ai-agents', label: 'Agentes de IA', href: '/automation/ai-agents', iconName: 'zap', group: 'Automação', keywords: ['ia', 'inteligencia', 'agente', 'ai', 'chatbot'] },
  { id: 'databases', label: 'Bases de Dados', href: '/databases', iconName: 'database', group: 'Automação', keywords: ['database', 'banco', 'dados'] },

  // Análise
  { id: 'reports-crm', label: 'Relatórios CRM', href: '/reports/crm', iconName: 'bar-chart', group: 'Análise', keywords: ['relatorio', 'crm', 'leads'] },
  { id: 'reports-csat', label: 'Relatório de CSAT', href: '/reports/csat', iconName: 'star', group: 'Análise', keywords: ['relatorio', 'csat', 'satisfacao', 'nota', 'avaliacao', 'pesquisa'] },

  // Configurações
  { id: 'users', label: 'Usuários', href: '/settings/users', iconName: 'users', group: 'Configurações', keywords: ['usuario', 'operador', 'membro'] },
  { id: 'levels', label: 'Níveis de Acesso', href: '/settings/levels', iconName: 'shield', group: 'Configurações', keywords: ['nivel', 'permissao', 'acesso'] },
  { id: 'teams', label: 'Equipes', href: '/settings/teams', iconName: 'users', group: 'Configurações', keywords: ['equipe', 'time', 'grupo'] },
  { id: 'shifts', label: 'Turnos', href: '/settings/shifts', iconName: 'clock', group: 'Configurações', keywords: ['turno', 'horario', 'expediente'] },
  { id: 'company', label: 'Empresa', action: 'open-company-drawer', iconName: 'briefcase', group: 'Configurações', keywords: ['empresa', 'companhia', 'organizacao'] },
  { id: 'units', label: 'Unidades', href: '/settings/units', iconName: 'map-pin', group: 'Configurações', keywords: ['unidade', 'loja', 'filial', 'local'] },
  { id: 'gamification', label: 'Gamificação', href: '/settings/gamification', iconName: 'award', group: 'Configurações', keywords: ['gamificacao', 'pontos', 'ranking', 'nivel', 'niveis', 'pontuacao'] },
  { id: 'channels', label: 'Canais de Atendimento', href: '/settings/channels', iconName: 'message-circle', group: 'Configurações', keywords: ['canal', 'whatsapp', 'instagram'] },
  { id: 'billing', label: 'Cobrança', href: '/billing', iconName: 'credit-card', group: 'Configurações', keywords: ['plano', 'pagamento', 'fatura', 'assinatura'] },
  // 'integrations' REMOVIDO (2026-08-19): `/settings/integrations` nunca existiu
  // neste app — a tela vive no ADMIN. O item só levava ao NotFound.
  // Produtos, Pedidos, Chamadas e os relatórios de Vendas/Atendimento SAÍRAM em
  // 20/10/2026 pelo mesmo motivo: as rotas não existem.
];

/**
 * Item que a pessoa pode abrir — mesma regra da trava das telas e do menu
 * lateral (`lib/route-access.ts`). Antes a busca listava tudo para todos.
 */
const COMPANY_RULE: AccessRule = { module: 'company', action: 'read' };

/**
 * Groups de itens
 */
const groups = ['Navegação', 'Comercial', 'Produtividade', 'Automação', 'Análise', 'Configurações'];

/**
 * CommandPalette - Modal de busca rápida (Cmd+K / Ctrl+K)
 *
 * Permite navegar rapidamente para qualquer página do sistema
 * digitando o nome ou palavras-chave.
 */
export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { open: openCompanyDrawer } = useCompanyDrawer();
  const { canAccessPath, can } = usePermissions();
  const visibleItems = navigationItems.filter((item) => {
    if (item.action === 'open-company-drawer') return can(COMPANY_RULE);
    return item.href ? canAccessPath(item.href) : false;
  });

  // Atalho Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Abertura programática (ex.: botão de busca da m-appbar mobile — R0.4)
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('troia:open-command-palette', onOpen);
    return () => window.removeEventListener('troia:open-command-palette', onOpen);
  }, []);

  const handleSelect = useCallback((item: CommandItem) => {
    setOpen(false);
    if (item.action === 'open-company-drawer') {
      openCompanyDrawer();
      return;
    }
    if (item.href) navigate(item.href);
  }, [navigate, openCompanyDrawer]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[20vh]"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)' }}
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-lg rounded-xl overflow-hidden"
        style={{
          backgroundColor: '#ffffff',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Command
          label="Busca rápida"
          shouldFilter={true}
        >
          {/* Search Input */}
          <div
            className="flex items-center gap-3 px-4 border-b"
            style={{ borderColor: '#e5e7eb' }}
          >
            <Icon
              name="search"
              className="h-4 w-4 flex-shrink-0"
              style={{ color: '#9ca3af' }}
            />
            <Command.Input
              placeholder="Buscar páginas, configurações..."
              className="w-full py-3.5 text-sm outline-none bg-transparent"
              style={{ color: '#1e293b' }}
              autoFocus
            />
            <kbd
              className="hidden sm:inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{
                color: '#9ca3af',
                backgroundColor: '#f1f5f9',
                border: '1px solid #e5e7eb',
              }}
            >
              ESC
            </kbd>
          </div>

          {/* Results */}
          <Command.List
            className="max-h-[320px] overflow-y-auto py-2 px-2"
          >
            <Command.Empty
              className="py-8 text-center text-sm"
              style={{ color: '#9ca3af' }}
            >
              Nenhum resultado encontrado.
            </Command.Empty>

            {groups.map((group) => {
              const groupItems = visibleItems.filter((item) => item.group === group);
              if (groupItems.length === 0) return null;

              return (
                <Command.Group
                  key={group}
                  heading={group}
                >
                  <div
                    className="px-2 py-1.5 text-[11px] font-semibold tracking-wider uppercase"
                    style={{ color: '#9ca3af' }}
                  >
                    {group}
                  </div>
                  {groupItems.map((item) => (
                    <Command.Item
                      key={item.id}
                      value={`${item.label} ${item.keywords?.join(' ') || ''}`}
                      onSelect={() => handleSelect(item)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors"
                      style={{ color: '#1e293b' }}
                      data-selected={undefined}
                    >
                      <Icon
                        name={item.iconName}
                        className="h-4 w-4 flex-shrink-0"
                        style={{ color: '#64748b' }}
                      />
                      <span className="flex-1">{item.label}</span>
                      <Icon
                        name="arrow-right"
                        className="h-3 w-3 flex-shrink-0 opacity-0 group-data-[selected=true]:opacity-100"
                        style={{ color: '#9ca3af' }}
                      />
                    </Command.Item>
                  ))}
                </Command.Group>
              );
            })}
          </Command.List>

          {/* Footer */}
          <div
            className="flex items-center justify-between px-4 py-2.5 border-t text-[11px]"
            style={{
              borderColor: '#e5e7eb',
              color: '#9ca3af',
              backgroundColor: '#f9fafb',
            }}
          >
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd
                  className="inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: '#e5e7eb', color: '#64748b' }}
                >
                  ↑↓
                </kbd>
                navegar
              </span>
              <span className="flex items-center gap-1">
                <kbd
                  className="inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: '#e5e7eb', color: '#64748b' }}
                >
                  ↵
                </kbd>
                selecionar
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd
                className="inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium"
                style={{ backgroundColor: '#e5e7eb', color: '#64748b' }}
              >
                Ctrl+K
              </kbd>
              abrir/fechar
            </span>
          </div>
        </Command>
      </div>
    </div>,
    document.body
  );
};

CommandPalette.displayName = 'CommandPalette';
